import { Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from './hooks/useAuth';
import { storeTokens } from './services/api';

// Layouts
import PublicLayout from './components/layouts/PublicLayout';
import DashboardLayout from './components/layouts/DashboardLayout';
import AdminLayout from './components/layouts/AdminLayout';
import { EnvironmentProvider } from './context/EnvironmentContext';

// Public Pages
import HomePage from './pages/public/HomePage';
import PublicCatalogPage from './pages/public/PublicCatalogPage';
import LoginPage from './pages/public/LoginPage';
import RegisterPage from './pages/public/RegisterPage';
import ForgotPasswordPage from './pages/public/ForgotPasswordPage';
import ResetPasswordPage from './pages/public/ResetPasswordPage';
import SetupProfilePage from "./pages/public/SetupProfilePage";
import SetupAccountPage from "./pages/public/SetupAccountPage";
import OfferPage from './pages/public/OfferPage';
import SingleProductOfferPage from './pages/public/SingleProductOfferPage';
import HomePageV2 from './pages/public/HomePageV2';
import TermsPage from './pages/public/TermsPage';
import PrivacyPage from './pages/public/PrivacyPage';
import HipaaPage from './pages/public/HipaaPage';
import StatesPage from './pages/public/StatesPage';
import ReturnsPage from './pages/public/ReturnsPage';
import PartnersPage from './pages/public/PartnersPage';
import GroupsPage from "./pages/public/GroupsPage";
import AffiliatesPage from "./pages/public/AffiliatesPage";
import TelehealthConsentPage from "./pages/public/TelehealthConsentPage";
import CalendarDemo from "./pages/public/CalendarDemo";
import UploadMembersPage from "./pages/public/UploadMembersPage";
import GiftRedeemPage from "./pages/public/GiftRedeemPage";
import GiftAccountCreatePage from "./pages/public/GiftAccountCreatePage";

// User Pages
import DashboardPage from './pages/user/DashboardPage';
import CatalogPage from './pages/user/CatalogPage';
import CategoriesPage from './pages/user/CategoriesPage';
import ProductsPage from './pages/user/ProductsPage';
import ProductDetailPage from './pages/user/ProductDetailPage';
import ProfilePage from './pages/user/ProfilePage';
import CompleteProfilePage from './pages/user/CompleteProfilePage';
import OrdersPage from './pages/user/OrdersPage';
import NotificationsPage from './pages/user/NotificationsPage';
import CheckoutPage from './pages/user/CheckoutPage';
import CheckoutSuccessPage from './pages/user/CheckoutSuccessPage';
import IntakePage from './pages/user/IntakePage';
import LabUploadPage from './pages/user/LabUploadPage';
import DoctorNowSchedulePage from './pages/user/DoctorNowSchedulePage';
import SupportPage from './pages/user/SupportPage';
import ResourcesPage from './pages/user/ResourcesPage';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import MembersPage from './pages/admin/MembersPage';
import ClientDetailPage from './pages/admin/ClientDetailPage';
import AdminProductsPage from './pages/admin/ProductsPage';
import AdminOrdersPage from './pages/admin/OrdersPage';
import OrderDetailPage from './pages/admin/OrderDetailPage';
import IntakeFormsPage from './pages/admin/IntakeFormsPage';
import ImportPage from './pages/admin/ImportPage';
import SettingsPage from './pages/admin/SettingsPage';
import ActivityLogsPage from './pages/admin/ActivityLogsPage';
import RolesPage from './pages/admin/RolesPage';
import WelcomeCallsPage from './pages/admin/WelcomeCallsPage';
import AbandonedIntakesPage from './pages/admin/AbandonedIntakesPage';
import SupportTicketsPage from './pages/admin/SupportTicketsPage';
import TasksPage from './pages/admin/TasksPage';
import SubscriptionPlansPage from './pages/admin/SubscriptionPlansPage';
import CSVPagesPage from './pages/admin/CSVPagesPage';
import CSVUploadsPage from './pages/admin/CSVUploadsPage';
import CSVReceivedPage from './pages/admin/CSVReceivedPage';
import EligibilityUploadsPage from './pages/admin/EligibilityUploadsPage';
import PromoCodesPage from './pages/admin/PromoCodesPage';
import GiftCardsPage from './pages/admin/GiftCardsPage';
import DataImportsPage from './pages/admin/DataImportsPage';
import OffersCreditsPage from './pages/admin/OffersCreditsPage';
import AccountsReceivablePage from './pages/admin/AccountsReceivablePage';
import TransactionsPage from './pages/admin/TransactionsPage';
import OvalCareSyncPage from './pages/admin/OvalCareSyncPage';
import RevenueCalculationPage from './pages/admin/RevenueCalculationPage';
import AdminResourcesPage from './pages/admin/ResourcesPage';
import AgenciesPage from './pages/admin/AgenciesPage';
import AgencyDetailPage from "./pages/admin/AgencyDetailPage";
import BlogListPage from './pages/public/BlogListPage';
import BlogPostPage from './pages/public/BlogPostPage';
import CSVUploadPage from './pages/public/CSVUploadPage';
import CSVLiveViewPage from './pages/public/CSVLiveViewPage';
import ApiDocsPage from './pages/public/ApiDocsPage';
import SsoErrorPage from './pages/public/SsoErrorPage';
import EligibilityDocsPage from './pages/public/EligibilityDocsPage';

// Components
import LoadingSpinner from './components/common/LoadingSpinner';

// Card 7237 — the controlled-substance lab gate below needs the product's flag and
// the customer's submission. The DECISION itself is pure and lives in the util, so
// this router keeps only the effect.
import * as productService from './services/productService';
import * as intakeService from './services/intakeService';
import { isControlledSubstanceProduct, isCheckoutBlockedPendingLabs } from './utils/intakeRouting';
import PermissionRoute from './components/routing/PermissionRoute';
import { useDocumentTitle } from './hooks/useDocumentTitle';

// Protected Route Wrapper
const ProtectedRoute = ({ children, requireAdmin = false, skipProfileGate = false }) => {
  const { user, isLoading, isDemo, drCareMemberId, isProfileComplete, isAdmin } = useAuth();
  // Card 54613: carry the destination so a signed-out deep link lands back on it after sign-in.
  // Read unconditionally, BEFORE the early returns — a hook after a return is a conditional hook.
  const { pathname, search } = useLocation();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!user) {
    // `LoginPage` re-validates this value before navigating (single leading `/`, not `//`), so
    // encoding it here is a transport concern only — it is never trusted as a redirect target.
    const intended = `${pathname}${search}`;
    return <Navigate to={`/login?redirect=${encodeURIComponent(intended)}`} replace />;
  }

  // Gate: if user has no DrCare telemed ID, force profile completion (skip for admins, staff, and demo)
  const isStaffUser = user.role === "staff" || user.roleRefId || user.role_ref_id;
  if (!skipProfileGate && !drCareMemberId && !isProfileComplete && !isDemo && !isStaffUser && user.role !== "admin" && user.role !== "super_admin" && user.role !== "agency_admin") {
    return <Navigate to="/complete-profile" replace />;
  }

  // Admin gate: the SERVER decides. `isAdmin` comes from /auth/me, where it is
  // resolved from BOTH users.role and users.role_ref_id -> roles /
  // role_permissions (api/middleware/auth.js resolveAccessContext — the same
  // resolver behind requireAdmin). No role-string list lives here any more, so
  // a custom RBAC role that genuinely grants admin reaches /admin, and the UI
  // can never disagree with what /api/admin/* will actually answer.
  // isAdmin is false until /auth/me answers → fails closed.
  if (requireAdmin && (isDemo || !isAdmin)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Controlled-substance lab gate (board card 7237).
//
// The disabled Continue button on /lab-upload is a UX affordance; THIS is the gate,
// because a customer can simply type /checkout?product=<slug>. It refuses checkout
// for a CONTROLLED product whose intake submission carries no lab document, and
// sends them back to the step.
//
// Fails CLOSED by construction: `isCheckoutBlockedPendingLabs` is asked with an
// `undefined` submission while the fetch is in flight, and `undefined` has no lab
// document, so a controlled product can never flash checkout mid-request.
//
// A product we could not READ is deliberately NOT blocked — inventing a gate from a
// failed lookup would strand every customer behind a transient API error, and
// CheckoutPage's own guards still apply.
const LabGate = ({ children }) => {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('product');
  const [status, setStatus] = useState({ checking: true, blocked: false });

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      if (!slug) {
        if (!cancelled) setStatus({ checking: false, blocked: false });
        return;
      }
      try {
        const { product } = await productService.getProductBySlug(slug);
        if (!isControlledSubstanceProduct(product)) {
          if (!cancelled) setStatus({ checking: false, blocked: false });
          return;
        }
        // A 404 here means "no intake for this product yet", which is itself a
        // reason to block — hence null rather than a rethrow.
        const submission = await intakeService.getMySubmission(product.id).catch(() => null);
        if (!cancelled) {
          setStatus({ checking: false, blocked: isCheckoutBlockedPendingLabs(product, submission) });
        }
      } catch {
        if (!cancelled) setStatus({ checking: false, blocked: false });
      }
    };

    check();
    return () => { cancelled = true; };
  }, [slug]);

  if (status.checking) return <LoadingSpinner fullScreen />;
  if (status.blocked) return <Navigate to={`/lab-upload?${searchParams.toString()}`} replace />;

  return children;
};

// Guest Route Wrapper (redirect if logged in)
const GuestRoute = ({ children }) => {
  const { user, isLoading, isAdmin } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (user) {
    // Same server-provided capability as the admin gate — an already-signed-in
    // admin lands on /admin, everyone else on /dashboard. Landing on /admin
    // without the capability would just bounce back off ProtectedRoute.
    return <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace />;
  }

  return children;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Gives every route its own <title> (card 41957). Mounted here, beside ScrollToTop, rather
// than in a layout because `/login` and `/register` render standalone OUTSIDE PublicLayout —
// a per-layout call would leave exactly those two on the generic brand title.
const DocumentTitle = () => {
  useDocumentTitle();
  return null;
};


// SSO Token Handler - detects sso_token query param, stores JWT, then renders children
const SsoTokenHandler = ({ children }) => {
  const location = useLocation();
  const { refreshUser } = useAuth();
  const hasSsoToken = new URLSearchParams(location.search).has('sso_token');
  const [processing, setProcessing] = useState(hasSsoToken);

  useEffect(() => {
    if (!hasSsoToken) return;
    const params = new URLSearchParams(location.search);
    const ssoToken = params.get('sso_token');
    // Decode JWT payload to get expiry, then store with proper expiry
    try {
      const payload = JSON.parse(atob(ssoToken.split('.')[1]));
      storeTokens(ssoToken, null, payload.exp);
    } catch {
      storeTokens(ssoToken, null, Math.floor(Date.now() / 1000) + 86400);
    }
    window.history.replaceState({}, '', location.pathname);
    refreshUser().finally(() => setProcessing(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (processing) return <LoadingSpinner fullScreen />;
  return children;
};

const App = () => (
  <EnvironmentProvider>
  <ScrollToTop />
  <DocumentTitle />
  <Routes>
    {/* Login Page - No Layout (standalone) */}
    <Route
      path="/login"
      element={
        <GuestRoute>
          <LoginPage />
        </GuestRoute>
      }
    />

    {/* Forgot Password Page */}
    <Route
      path="/forgot-password"
      element={
        <GuestRoute>
          <ForgotPasswordPage />
        </GuestRoute>
      }
    />

    {/* Reset Password Page - NOT in GuestRoute (Supabase reset link auto-authenticates) */}
    <Route
      path="/reset-password"
      element={<ResetPasswordPage />}
    />
    <Route
      path="/setup-profile"
      element={
        <GuestRoute>
          <SetupProfilePage />
        </GuestRoute>
      }
    />
    <Route
      path="/setup-account"
      element={<SetupAccountPage />}
    />

    {/* Register Page */}
    <Route
      path="/register"
      element={
        <GuestRoute>
          <RegisterPage />
        </GuestRoute>
      }
    />

    {/* Public Routes */}
    {/* Standalone pages (own nav/footer) */}
    <Route path="/partners" element={<PartnersPage />} />
    <Route path="/groups" element={<GroupsPage />} />
    <Route path="/api-docs" element={<ApiDocsPage />} />
    <Route path="/docs/eligibility-file" element={<EligibilityDocsPage />} />
    <Route path="/telehealth-consent" element={<TelehealthConsentPage />} />
    <Route path="/calendar-demo" element={<CalendarDemo />} />
    <Route path="/calendar" element={<CalendarDemo />} />
    <Route path="/schedule" element={<CalendarDemo />} />
    <Route path="/sso-error" element={<SsoErrorPage />} />
    {/* Card 54613 — the partner console is behind the partner sign-in, not a ?token= link.
        Signed out → /login?redirect=/upload-members (ProtectedRoute); signed in without
        `member_upload.view` → PermissionRoute's AccessDenied panel. */}
    <Route
      path="/upload-members"
      element={
        <ProtectedRoute>
          <PermissionRoute permission="member_upload.view">
            <UploadMembersPage />
          </PermissionRoute>
        </ProtectedRoute>
      }
    />

    {/* Gift Membership Redeem/Claim - Public (no auth, standalone) */}
    <Route path="/gift/:code/create-account" element={<GiftAccountCreatePage />} />
    <Route path="/gift/:code" element={<GiftRedeemPage />} />
    <Route path="/claim/:code" element={<GiftRedeemPage />} />

    <Route element={<PublicLayout />}>
      <Route path="/" element={<HomePage />} />
      <Route path="/v2" element={<HomePageV2 />} />
      <Route path="/categories" element={<CategoriesPage />} />
      <Route path="/categories/:slug" element={<ProductsPage />} />
      <Route path="/products/:slug" element={<ProductDetailPage />} />
      <Route path="/offers/:slug" element={<OfferPage />} />
      <Route path="/offer/:slug" element={<OfferPage />} />
      <Route path="/offer/product/:productId" element={<SingleProductOfferPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/hipaa" element={<HipaaPage />} />
      <Route path="/states" element={<StatesPage />} />
      <Route path="/returns" element={<ReturnsPage />} />
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/treatments" element={<PublicCatalogPage />} />
      <Route path="/affiliates" element={<AffiliatesPage />} />
    </Route>

    {/* User Dashboard Routes */}
    <Route
      element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }
    >
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/catalog" element={<CatalogPage />} />
      <Route path="/profile" element={<ProfilePage />} />
      <Route path="/orders" element={<OrdersPage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/resources" element={<ResourcesPage />} />
      <Route path="/support" element={<SupportPage />} />
    </Route>

    {/* Checkout Page - Standalone (no sidebar/navbar) */}
    <Route
      path="/checkout"
      element={
        <ProtectedRoute>
          <LabGate>
            <CheckoutPage />
          </LabGate>
        </ProtectedRoute>
      }
    />

    {/* Lab Upload Step - Standalone (no sidebar/navbar). Controlled products only;
        the page itself hands a non-controlled product back to its normal route. */}
    <Route
      path="/lab-upload"
      element={
        <ProtectedRoute>
          <LabUploadPage />
        </ProtectedRoute>
      }
    />

    {/* Doctor Now appointment picker - Standalone (no sidebar/navbar). Card 28373.
        Only an appointment-required DoctorNow product routes here; the page itself
        hands anything else back to the route it would have taken. It is deliberately
        NOT behind LabGate: a controlled product stops at /lab-upload first, so it can
        only arrive here once its labs are already attached. */}
    <Route
      path="/doctor-now-schedule"
      element={
        <ProtectedRoute>
          <DoctorNowSchedulePage />
        </ProtectedRoute>
      }
    />

    {/* Intake Page - Standalone (no sidebar/navbar) */}
    <Route
      path="/intake/:slug"
      element={
        <ProtectedRoute>
          <IntakePage />
        </ProtectedRoute>
      }
    />

    {/* Checkout Success Page - Standalone (no sidebar/navbar) */}
    <Route
      path="/checkout/success"
      element={
        <ProtectedRoute>
          <CheckoutSuccessPage />
        </ProtectedRoute>
      }
    />

    {/* CSV Upload Page - Public (standalone, no nav) */}
    <Route path="/csv/:slug" element={<CSVUploadPage />} />

    {/* CSV Live Data View - Public (standalone, no nav) */}
    <Route path="/data/:slug/:uploadId" element={<CSVLiveViewPage />} />

    {/* Admin Routes */}
    <Route
      path="/admin"
      element={
        <SsoTokenHandler>
          <ProtectedRoute requireAdmin>
            <AdminLayout />
          </ProtectedRoute>
        </SsoTokenHandler>
      }
    >
      <Route index element={<AdminDashboard />} />
      <Route path="members" element={<PermissionRoute permission="clients.view"><MembersPage /></PermissionRoute>} />
      <Route path="members/:id" element={<PermissionRoute permission="clients.view"><ClientDetailPage /></PermissionRoute>} />
      <Route path="members/import" element={<PermissionRoute permission="clients.edit"><ImportPage /></PermissionRoute>} />
      <Route path="agencies" element={<PermissionRoute permission="agencies.view"><AgenciesPage /></PermissionRoute>} />
      <Route path="agencies/:id" element={<PermissionRoute permission="agencies.view"><AgencyDetailPage /></PermissionRoute>} />
      <Route path="products" element={<PermissionRoute permission="products.view"><AdminProductsPage /></PermissionRoute>} />
      <Route path="orders" element={<PermissionRoute permission="orders.view"><AdminOrdersPage /></PermissionRoute>} />
      <Route path="orders/:id" element={<PermissionRoute permission="orders.view"><OrderDetailPage /></PermissionRoute>} />
      <Route path="intake-forms" element={<PermissionRoute permission="intake_forms.view"><IntakeFormsPage /></PermissionRoute>} />
      {/* Consolidated hubs (REQ-10, REQ-11). Each composes its existing pages by import,
          so the standalone routes below stay registered: a bookmarked legacy URL keeps
          resolving (REQ-16) and every page stays independently renderable for rollback
          (REQ-57). The route gate is the SAME permission that decides whether the nav
          item is shown (AdminLayout.jsx:288 / :305), so a role can never see an entry it
          cannot open; each tab keeps its own gate inside the hub. */}
      <Route path="data-imports" element={<PermissionRoute permission="csv_received.view"><DataImportsPage /></PermissionRoute>} />
      <Route path="offers" element={<PermissionRoute permission="promo_codes.view"><OffersCreditsPage /></PermissionRoute>} />
      <Route path="promo-codes" element={<PermissionRoute permission="promo_codes.view"><PromoCodesPage /></PermissionRoute>} />
      <Route path="gift-cards" element={<PermissionRoute permission="gift_cards.view"><GiftCardsPage /></PermissionRoute>} />
      <Route path="accounts-receivable" element={<PermissionRoute permission="accounts_receivable.view"><AccountsReceivablePage /></PermissionRoute>} />
      <Route path="transactions" element={<PermissionRoute permission="orders.view"><TransactionsPage /></PermissionRoute>} />
      <Route path="oval-care-sync" element={<PermissionRoute permission="oval_care_sync.view"><OvalCareSyncPage /></PermissionRoute>} />
      <Route path="revenue-calculation" element={<PermissionRoute permission="revenue_calculation.view"><RevenueCalculationPage /></PermissionRoute>} />
      <Route path="resources" element={<PermissionRoute permission="resources.view"><AdminResourcesPage /></PermissionRoute>} />
      <Route path="csv-pages" element={<PermissionRoute permission="csv_pages.view"><CSVPagesPage /></PermissionRoute>} />
      <Route path="csv-uploads" element={<PermissionRoute permission="csv_uploads.view"><CSVUploadsPage /></PermissionRoute>} />
      <Route path="csv-received" element={<PermissionRoute permission="csv_received.view"><CSVReceivedPage /></PermissionRoute>} />
      <Route path="eligibility-uploads" element={<PermissionRoute permission="eligibility.view"><EligibilityUploadsPage /></PermissionRoute>} />
      <Route path="support-tickets" element={<PermissionRoute permission="support_tickets.view"><SupportTicketsPage /></PermissionRoute>} />
      <Route path="tasks" element={<PermissionRoute permission="tasks.view"><TasksPage /></PermissionRoute>} />
      <Route path="settings" element={<PermissionRoute permission="settings.view"><SettingsPage /></PermissionRoute>} />
      <Route path="activity-logs" element={<PermissionRoute permission="activity_logs.view"><ActivityLogsPage /></PermissionRoute>} />
      <Route path="plans" element={<PermissionRoute permission="plans.view"><SubscriptionPlansPage /></PermissionRoute>} />
      <Route path="roles" element={<PermissionRoute permission="roles.view"><RolesPage /></PermissionRoute>} />
      <Route path="welcome-calls" element={<PermissionRoute permission="welcome_calls.view"><WelcomeCallsPage /></PermissionRoute>} />
      <Route path="abandoned-intakes" element={<PermissionRoute permission="abandoned_intakes.view"><AbandonedIntakesPage /></PermissionRoute>} />
    </Route>

    {/* Catch all */}

        <Route path="/complete-profile" element={
          <ProtectedRoute skipProfileGate>
            <CompleteProfilePage />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>
  </EnvironmentProvider>
);

export default App;
